
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { alpha, Box, Grid2, List, ListItem, Paper, sliderClasses, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    DatePicker,
    DefaultFooterPosition,
    DisplayField,
    FormBreadcrumbs,
    GroupedField,
    GroupedFieldLabel,
    GroupedFields,
    IDisplayFieldComponentProps,
    InputOptionMode,
    InstitutionContext,
    MarkdownView,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RadioGroup,
    RouteLoading,
    Slider,
    ToggleButtonGroup,
    UserType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FunctionComponent, useContext, useMemo } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { useTranslation } from 'react-i18next';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import TranslatedFooter from './templates/TranslatedFooter';
import { TranslatedProForm } from './TranslatedProForm';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IEq5d5lFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const SliderFlexBox  = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center'
}));

const SliderPaper  = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',

    '& > :nth-of-type(2)': {
        marginLeft: 32
    }
}));

const SliderExtra  = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    fontWeight: 'bold',

    '&:first-of-type': {
        paddingBottom: theme.spacing(2)
    },

    '&:last-of-type': {
        paddingTop: theme.spacing(2)
    }
}));

const SliderWrapper = styled(Box)(({ theme }) => ({
    [`& .${sliderClasses.root}`]: {
        width: 2,
        padding: '0 15px',
        marginRight: 32
    },
    [`& .${sliderClasses.valueLabel}`]: {
        top: 'calc(-50% + 12px) !important',
        left: '-22px !important',
        '& *': {
            background: 'transparent',
            color: '#000',
        },
    },
    [`& .${sliderClasses.rail}`]: {
        width: '10px !important',
        borderRadius: 0
    },
    [`& .${sliderClasses.track}`]: {
        width: '10px !important',
        borderRadius: 0
    },
    [`& .${sliderClasses.mark}`]: {
        backgroundColor: alpha(theme.palette.primary.main, 0.3),
        width: 20,
        height: 1,
        transform: 'translate(-50%, -1px)',
        '&[data-index="0"]': {
            width: 20,
            height: 20,
            borderRadius: 12,
            transform: 'translate(-50%, 18px)',
            opacity: 1,
            backgroundColor: theme.palette.primary.main,
        },
        '&[data-index="2"],&[data-index="4"],&[data-index="6"],&[data-index="8"],&[data-index="10"],&[data-index="12"],&[data-index="14"],&[data-index="16"],&[data-index="18"]': {
            width: 30,
        },
        '&[data-index="20"]': {
            width: 30,
            height: 2,
            transform: 'translate(-50%, 0px)',
        }
    },
    [`& .${sliderClasses.markLabel}`]: {
        left: '40px !important'
    },
    [`& .${sliderClasses.markLabelActive}`]: {
        opacity: 1,
    },
    [`& .${sliderClasses.thumb}`]: {
        width: '10px',
        height: '0px',
        borderRadius: 0
    }
}))



const YourHealthTodayPaper  = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    width: 120,
    height: 100,
    textAlign: 'center'
}));

const YourHealthTodayTypography  = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white
}));


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const marks = [
    {
        value: 0,
        label: '0'
    },
    {
        value: 5,
        label: '5'
    },
    {
        value: 10,
        label: '10'
    },
    {
        value: 15,
        label: '15'
    },
    {
        value: 20,
        label: '20'
    },
    {
        value: 25,
        label: '25'
    },
    {
        value: 30,
        label: '30'
    },
    {
        value: 35,
        label: '35'
    },
    {
        value: 40,
        label: '40'
    },
    {
        value: 45,
        label: '45'
    },
    {
        value: 50,
        label: '50'
    },
    {
        value: 55,
        label: '55'
    },
    {
        value: 60,
        label: '60'
    },
    {
        value: 65,
        label: '65'
    },
    {
        value: 70,
        label: '70'
    },
    {
        value: 75,
        label: '75'
    },
    {
        value: 80,
        label: '80'
    },
    {
        value: 85,
        label: '85'
    },
    {
        value: 90,
        label: '90'
    },
    {
        value: 95,
        label: '95'
    },
    {
        value: 100,
        label: '100'
    }
];


const YourHealthToday: React.FunctionComponent<IDisplayFieldComponentProps> = ({
    fieldState: { value }
}) => {
    return (
        <YourHealthTodayPaper>
            <GroupedFieldLabel simpleLabel />
            <YourHealthTodayTypography
                variant="h3"
                variantMapping={{
                    h3: 'div'
                }}
            >
                {value}
            </YourHealthTodayTypography>
        </YourHealthTodayPaper>
    );
};

const Eq5d5lForm: FunctionComponent<IEq5d5lFormProps> = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const isPatient = React.useMemo(() => {
        return user?.type === UserType.Patient;
    }, [user]);

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const sliderInstruction1 = useMemo(() => {
        const translated = t("forms.eq5d5l.sliderInstruction.1", {defaultValue: null});

        if(translated == "forms.eq5d5l.sliderInstruction.1") {
            return null;
        }

        return translated;
    }, [t]);

    const sliderInstruction2 = useMemo(() => {
        const translated = t("forms.eq5d5l.sliderInstruction.2", {defaultValue: null});

        if(translated == "forms.eq5d5l.sliderInstruction.2") {
            return null;
        }

        return translated;
    }, [t]);

    const sliderInstruction3 = useMemo(() => {
        const translated = t("forms.eq5d5l.sliderInstruction.3", {defaultValue: null});

        if(translated == "forms.eq5d5l.sliderInstruction.3") {
            return null;
        }

        return translated;
    }, [t]);

    const sliderInstruction4 = useMemo(() => {
        const translated = t("forms.eq5d5l.sliderInstruction.4", {defaultValue: null});

        if(translated == "forms.eq5d5l.sliderInstruction.4") {
            return null;
        }

        return translated;
    }, [t]);
    

    const sliderHeaderText = useMemo(() => {
        const translated = t("forms.eq5d5l.sliderHeaderText", {defaultValue: null});

        if(translated == "forms.eq5d5l.sliderHeaderText") {
            return null;
        }

        return translated;
    }, [t]);
    

    const sliderFooterText = useMemo(() => {
        const translated = t("forms.eq5d5l.sliderFooterText", {defaultValue: null});

        if(translated == "forms.eq5d5l.sliderFooterText") {
            return null;
        }

        return translated;
    }, [t]);

    const {
        permissions: [
            canUpdatePro, 
            canAdministerPro
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsProUpdate,
            Permission.OpmsProAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const showSteps = isPatient || isMobile;

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <TranslatedProForm
                formTranslationKey={"forms.eq5d5l"}
                validateOn={ValidateOn.onChange}
                useSteps={showSteps}
                readOnly={showSteps ? !canUpdatePro : !canAdministerPro}
                FooterOptions={{
                    content: <TranslatedFooter isPatient={isPatient} />,
                    position: DefaultFooterPosition
                }}
                DialogOptions={AppSettings.proForms.dialogOptions}
                showEventHeading
                debug={AppSettings.debug}
                disableEntityTitleCase
                disableEntityLowerCase
            >
                {
                    !isPatient && (
                        <GroupedFields>
                            <GroupedField
                                propertyPath="dateCompleted"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                            <GroupedField
                                LabelProps={{
                                    label: <></>
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.Auto
                                }}
                                propertyPath="ptDidNotComplete"
                                component={Checkbox}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                        </GroupedFields>
                    )
                }
                {
                    showSteps && isExtraSmall ?
                        <GroupedField
                            propertyPath="mobilityScore"
                            component = {ToggleButtonGroup}
                            orientation="vertical"
                            spacing={3}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        /> :
                        <GroupedField
                            propertyPath="mobilityScore"
                            component={RadioGroup}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        />
                    
                }
                {
                    showSteps && isExtraSmall ?
                        <GroupedField
                            propertyPath="selfCareScore"
                            component={ToggleButtonGroup}
                            orientation="vertical"
                            spacing={ 3 }
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        /> :
                        <GroupedField
                            propertyPath="selfCareScore"
                            component={RadioGroup}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        />
                }
                {
                    showSteps && isExtraSmall ?
                        <GroupedField
                            propertyPath="usualActivitiesScore"
                            component = {ToggleButtonGroup}
                            orientation = "vertical"
                            spacing = { 3 }
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        /> :
                        <GroupedField
                            propertyPath="usualActivitiesScore"
                            component={RadioGroup}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        />   
                }
                {
                    showSteps && isExtraSmall ?
                        <GroupedField
                            propertyPath="discomfortScore"
                            component = {ToggleButtonGroup}
                            orientation = "vertical"
                            spacing = { 3 }
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        /> :
                        <GroupedField
                            propertyPath="discomfortScore"
                            component={RadioGroup}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        />
                }
                {
                    showSteps && isExtraSmall ?
                        <GroupedField
                            propertyPath="anxietyDepressionScore"
                            component = {ToggleButtonGroup}
                            orientation = "vertical"
                            spacing = { 3 }
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        /> :
                        <GroupedField
                            propertyPath="anxietyDepressionScore"
                            component={RadioGroup}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }
                            }}
                        />
                }
                <GroupedFields>
                    <Box>
                        <List>
                            <ListItem>
                                <Typography>
                                    {
                                        !!sliderInstruction1 && 
                                        <MarkdownView>{sliderInstruction1}</MarkdownView> 
                                    }
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography>
                                    {
                                        !!sliderInstruction2 && 
                                        <MarkdownView>{sliderInstruction2}</MarkdownView> 
                                    }
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography>
                                    {
                                        !!sliderInstruction3 && 
                                        <MarkdownView>{sliderInstruction3}</MarkdownView> 
                                    }
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography>
                                    {
                                        !!sliderInstruction4 && 
                                        <MarkdownView>{sliderInstruction4}</MarkdownView> 
                                    }
                                </Typography>
                            </ListItem>
                        </List>
                    </Box> 
                    <Grid2
                        container
                        alignItems="center"
                    >
                        <Grid2
                            size={{
                                xs: 8
                            }}
                        >
                            <SliderFlexBox>
                                <SliderPaper>
                                    <SliderExtra>
                                        {sliderHeaderText}
                                    </SliderExtra>
                                    <GroupedField
                                        propertyPath="healthScore"
                                        WrappingComponent={({...props}) => <SliderWrapper {...props}/>}
                                        component={Slider}
                                        FormControlProps={{
                                            sx: {
                                                height: 500
                                            }
                                        }}
                                        orientation="vertical"
                                        marks={marks}
                                        track="normal"
                                        LabelProps={{
                                            hide: true
                                        }}
                                        step={1}
                                        min={0}
                                        max={100}
                                        FieldGridProps={{
                                            size: {
                                                xs: 12,
                                                sm: 12,
                                                md: 12,
                                                lg: 12,
                                                xl: 12
                                            },
                                            sx:{
                                                display: 'flex',
                                                justifyContent: 'center'
                                            }
                                        }}
                                    />
                                    <SliderExtra>
                                        {sliderFooterText}
                                    </SliderExtra>
                                </SliderPaper>
                            </SliderFlexBox>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: 4
                            }}
                        >
                            <DisplayField 
                                propertyPath="healthScore" 
                                component={YourHealthToday} 
                                subscription={{ value: true }} 
                            />
                        </Grid2>
                    </Grid2>
                </GroupedFields>
                {
                    canAdministerPro && (
                        <SourceDocuments />
                    )
                }
            </TranslatedProForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default Eq5d5lForm;
